import {
    TerraDraw,
    TerraDrawPolygonMode,
    TerraDrawRectangleMode,
    TerraDrawAngledRectangleMode,
    TerraDrawSelectMode,
    TerraDrawCircleMode,
} from 'terra-draw';
import { TerraDrawGoogleMapsAdapter } from 'terra-draw-google-maps-adapter';

import {
    TERRA_DRAW_MODES,
    TERRA_DRAW_STYLES,
    TERRA_DRAW_CONFIG,
    getTerraDrawMode,
    convertGeoJSONToPolygon,
    isValidGeometry,
} from './terraDrawConstants';

export const createTerraDrawInstance = ({ map, google }) => {
    if (!map || !google) {
        throw new Error('Map and google objects are required');
    }

    const adapter = new TerraDrawGoogleMapsAdapter({
        map,
        lib: google.maps,
    });

    const mergedStyles = {
        polygon: { ...TERRA_DRAW_STYLES.polygon },
        rectangle: {
            ...TERRA_DRAW_STYLES.rectangle,
        },
        circle: { ...TERRA_DRAW_STYLES.circle },
    };

    const modes = [
        new TerraDrawSelectMode({
            styles: mergedStyles.select,
            ...TERRA_DRAW_CONFIG.select,
        }),
        new TerraDrawPolygonMode({
            styles: mergedStyles.polygon,
            ...TERRA_DRAW_CONFIG.polygon,
        }),
        new TerraDrawRectangleMode({
            styles: mergedStyles.rectangle,
            ...TERRA_DRAW_CONFIG.rectangle,
        }),
        new TerraDrawAngledRectangleMode({
            styles: mergedStyles.rectangle,
            ...TERRA_DRAW_CONFIG.rectangle,
        }),
        new TerraDrawCircleMode({
            styles: mergedStyles.circle,
            ...TERRA_DRAW_CONFIG.circle,
        }),
    ];

    return new TerraDraw({ adapter, modes });
};

export class TerraDrawController {
    constructor(terraDrawInstance) {
        this.terraDraw = terraDrawInstance;
        this.finishListener = null;
    }

    start() {
        if (!this.terraDraw.enabled) this.terraDraw.start();
    }

    stop() {
        if (this.terraDraw.enabled) this.terraDraw.stop();
    }

    getMode() {
        return this.terraDraw.getMode();
    }

    setMode(mode) {
        const currentMode = this.terraDraw.getMode();
        if (currentMode !== mode) this.terraDraw.setMode(mode);
    }

    setDrawingMode(drawingType) {
        const terraDrawMode = getTerraDrawMode(drawingType);
        if (!terraDrawMode) {
            console.warn(
                '[TerraDraw] No mode found for drawing type:',
                drawingType,
            );
            return;
        }
        this.start();
        this.setMode(terraDrawMode);
    }

    onFinish(callback) {
        this.removeFinishListener();
        this.finishListener = callback;
        this.terraDraw.on('finish', this.finishListener);
    }

    removeFinishListener() {
        if (this.finishListener) {
            try {
                this.terraDraw.off('finish', this.finishListener);
            } catch (error) {
                console.error(
                    '[TerraDraw] Error removing finish listener:',
                    error,
                );
            }
            this.finishListener = null;
        }
    }

    getFeature(featureId) {
        const snapshot = this.terraDraw.getSnapshot();
        return snapshot.find((f) => f.id === featureId) || null;
    }

    removeFeatures(featureIds) {
        if (featureIds && featureIds.length > 0) {
            try {
                this.terraDraw.removeFeatures(featureIds);
            } catch (error) {
                console.error('[TerraDraw] Error removing features:', error);
            }
        }
    }

    clear() {
        try {
            const snapshot = this.terraDraw.getSnapshot();
            if (snapshot && snapshot.length > 0) {
                this.terraDraw.clear();
            }
        } catch (error) {
            console.error('[TerraDraw] Error clearing features:', error);
        }
    }

    reset() {
        this.removeFinishListener();
        this.clear();
        if (this.terraDraw.enabled) this.setMode('static');
    }

    dispose() {
        this.reset();
        if (this.terraDraw.enabled) {
            this.terraDraw.stop();
        }
    }

    logStatus() {
        console.info('[TerraDraw] Status:', {
            enabled: this.enabled,
            mode: this.getMode(),
            hasFinishListener: !!this.finishListener,
            featuresCount: this.terraDraw.getSnapshot()?.length || 0,
        });
    }

    get enabled() {
        return this.terraDraw.enabled;
    }

    get instance() {
        return this.terraDraw;
    }
}

export const processFinishEvent = ({
    controller,
    featureId,
    onSuccess,
    onError = console.error,
}) => {
    try {
        const feature = controller.getFeature(featureId);

        if (!feature || !feature.geometry) {
            onError('Feature not found or has no geometry:', featureId);
            return;
        }

        const { geometry } = feature;

        if (!isValidGeometry(geometry)) {
            onError('Invalid geometry:', geometry);
            return;
        }

        const coords = geometry.coordinates[0];
        if (!coords || !Array.isArray(coords) || coords.length < 2) {
            onError('Invalid coordinates - need at least 2 points:', coords);
            return;
        }

        const polygon = convertGeoJSONToPolygon(geometry);

        if (polygon.length < 3) {
            onError('Polygon must have at least 3 points:', polygon);
            return;
        }

        onSuccess(polygon);
    } catch (error) {
        onError('Error processing finish event:', error, { featureId });
    }
};

export const createFinishHandler = ({
    controller,
    createSegment,
    onOpenDrawer,
    breakpoint,
}) => {
    return (featureId) => {
        processFinishEvent({
            controller,
            featureId,
            onSuccess: (polygon) => {
                createSegment(polygon);

                if (['xs', 'sm', 'md'].includes(breakpoint) && onOpenDrawer) {
                    onOpenDrawer();
                }
            },
        });
    };
};

export default {
    createTerraDrawInstance,
    TerraDrawController,
    processFinishEvent,
    createFinishHandler,
    TERRA_DRAW_MODES,
};
